import React, { useEffect, useState, useRef } from 'react'
import ama2 from "../images/ama2.jpeg";
import construction from "../images/construction.jpg";
import marine from "../images/marine.jpg";
import office from "../images/office.jpg";
import herowelcome from "../images/herowelcome.png";
import MouseParticles from './MouseParticles';
import { Link } from 'react-router-dom';


const Home: React.FC = () => {
    const [currentSlide, setCurrentSlide] = useState(0)
    const [scrollY, setScrollY] = useState(0)
    const [openFAQ, setOpenFAQ] = useState<number | null>(null)
    const [activeTab, setActiveTab] = useState(0)
    const statsRef = useRef<HTMLDivElement>(null)
    const [statsVisible, setStatsVisible] = useState(false)
    const [counters, setCounters] = useState({ years: 0, clients: 0, coverage: 0, satisfaction: 0 })

    const heroSlides = [
        {
            image: herowelcome,
            badge: 'Corporate & Business',
            title: 'Protecting What',
            highlight: 'Drives Business',
            sub: 'From property to liability — comprehensive corporate insurance solutions tailored to your operational risks.',
        },
        {
            image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1600&q=80',
            badge: 'Motor & Fleet',
            title: 'On the Road,',
            highlight: 'Always Covered',
            sub: 'Individual and fleet motor insurance with 24/7 roadside advocacy and claims support.',
        },
        {
            image: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=1600&q=80',
            badge: 'Home & Property',
            title: 'Your Home,',
            highlight: 'Our Commitment',
            sub: 'Complete property protection — from fire and allied perils to personal liability.',
        },
        {
            image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1600&q=80',
            badge: 'Travel Insurance',
            title: 'Travel Far,',
            highlight: 'Worry Less',
            sub: 'Comprehensive travel coverage with medical emergencies, trip cancellation, and baggage protection.',
        },
    ]

    const solutionTabs = [
        {
            label: 'Corporate & Business',
            icon: '🏗️',
            items: [
                { name: 'Property Insurance', desc: 'Fire & Allied Perils coverage for your business assets' },
                { name: 'Business Interruption', desc: 'Revenue protection when operations are disrupted' },
                { name: 'Public & Employer\'s Liability', desc: 'Legal liability protection for your workforce and public' },
                { name: 'Marine & Goods in Transit', desc: 'Cargo and marine hull insurance for trade' },
                { name: 'Engineering Insurance', desc: 'Contractors All Risk, Erection All Risk coverage' },
            ],
        },
        {
            label: 'Employee Benefits',
            icon: '👥',
            items: [
                { name: 'Group Life Insurance', desc: 'Financial security for your employees and dependants' },
                { name: 'Group Personal Accident', desc: 'Accident protection for your entire workforce' },
                { name: 'Workmen\'s Compensation', desc: 'Statutory cover for work-related injuries and illness' },
                { name: 'Health Insurance', desc: 'Corporate health schemes for employee wellness' },
            ],
        },
        {
            label: 'Personal Insurance',
            icon: '🛡️',
            items: [
                { name: 'Motor Insurance', desc: 'Comprehensive and third-party vehicle cover' },
                { name: 'Home & Property', desc: 'Protection for your residence and valuables' },
                { name: 'Travel Insurance', desc: 'International travel medical and trip cover' },
                { name: 'Personal Accident', desc: 'Individual accident and disability protection' },
                { name: 'Life Insurance', desc: 'Term life, whole life, and critical illness plans' },
            ],
        },
    ]

    const whyChooseUs = [
        { icon: '⚖️', title: 'Independence & Objectivity', desc: 'We work for you, not the insurer — giving unbiased recommendations that serve your best interests.' },
        { icon: '🎯', title: 'Industry Expertise', desc: 'Deep technical knowledge backed by years of professional experience across all insurance lines.' },
        { icon: '🔧', title: 'Tailored Risk Solutions', desc: 'Coverage structured around your specific operational needs, not off-the-shelf policies.' },
        { icon: '💬', title: 'Transparent Communication', desc: 'Clear advice and honest recommendations at every stage — no hidden fees, no surprises.' },
        { icon: '🤝', title: 'Strong Claims Support', desc: 'We stand with you and advocate on your behalf when it matters most.' },
        { icon: '💰', title: 'Cost Efficiency', desc: 'We negotiate competitive premiums without compromising on the adequacy of your protection.' },
        { icon: '🔗', title: 'Long-term Partnership', desc: 'We build enduring relationships rooted in trust, performance, and ongoing portfolio management.' },
    ]

    const faqs = [
        { q: 'How quickly can I get a quote?', a: 'We provide personalised quotes within 24 hours. For standard personal lines like motor and travel, our advisors can offer indicative pricing immediately during a consultation.' },
        { q: 'What makes Guardrisk different from other brokers?', a: 'As independent brokers, we work exclusively for you — not the insurer. This means every recommendation is driven by your needs. We combine industry expertise with ongoing portfolio management to ensure your cover always keeps pace with your risks.' },
        { q: 'Can I bundle multiple insurance products?', a: 'Absolutely. Bundling corporate, employee benefits, and personal insurance through Guardrisk allows us to negotiate better terms and simplifies your portfolio management significantly.' },
        { q: 'How do I file a claim?', a: 'Contact us immediately via phone, WhatsApp (+233 24 2876 315), or email. Our claims team will guide you through the process, prepare documentation, and advocate with the insurer on your behalf.' },
        { q: 'Do you serve businesses outside Ghana?', a: 'Our primary focus is Ghana and the West African market. For clients with regional or international operations, we can arrange cover through our network of international insurance partners.' },
        { q: 'Is my personal and business data secure?', a: 'Yes. All client information is handled with strict confidentiality under our privacy policy. We use secure communication channels and never share your data without express consent.' },
    ]

    useEffect(() => {
        const onScroll = () => setScrollY(window.scrollY)
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const interval = setInterval(() => setCurrentSlide(p => (p + 1) % heroSlides.length), 6500)
        return () => clearInterval(interval)
    }, [heroSlides.length])

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setStatsVisible(true) },
            { threshold: 0.3 }
        )
        if (statsRef.current) observer.observe(statsRef.current)
        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        if (!statsVisible) return
        const targets = { years: 15, clients: 5000, coverage: 2, satisfaction: 98 }
        const duration = 2000
        const steps = 60
        let step = 0
        const interval = setInterval(() => {
            step++
            const progress = step / steps
            const ease = 1 - Math.pow(1 - progress, 3)
            setCounters({
                years: Math.round(targets.years * ease),
                clients: Math.round(targets.clients * ease),
                coverage: Math.round(targets.coverage * ease * 10) / 10,
                satisfaction: Math.round(targets.satisfaction * ease),
            })
            if (step >= steps) clearInterval(interval)
        }, duration / steps)
        return () => clearInterval(interval)
    }, [statsVisible])

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('gr-visible'); observer.unobserve(e.target) } }),
            { threshold: 0.08, rootMargin: '0px 0px -60px 0px' }
        )
        document.querySelectorAll('.gr-reveal').forEach(el => observer.observe(el))
        return () => observer.disconnect()
    }, [])

    return (
        <div className="bg-white overflow-x-hidden">
            <MouseParticles />
            <style>{`
                .gr-reveal { opacity: 0; transform: translateY(32px); transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1); }
                .gr-reveal.gr-visible { opacity: 1; transform: none; }
                .gr-delay-1 { transition-delay: 0.1s; }
                .gr-delay-2 { transition-delay: 0.2s; }
                .gr-delay-3 { transition-delay: 0.3s; }
                .gr-delay-4 { transition-delay: 0.4s; }
                .gr-delay-5 { transition-delay: 0.5s; }
                .gr-delay-6 { transition-delay: 0.6s; }
                @keyframes gr-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
                @keyframes gr-pulse { 0%,100%{opacity:0.4} 50%{opacity:0.8} }
                @keyframes gr-marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }
                .gr-float { animation: gr-float 6s ease-in-out infinite; }
                .gr-pulse { animation: gr-pulse 3s ease-in-out infinite; }
                .gr-marquee-track { animation: gr-marquee 30s linear infinite; }
                .gr-slide-img { transition: opacity 1.2s cubic-bezier(.4,0,.2,1); }
                .gr-tab-btn { transition: all 0.25s ease; }
                .gr-faq-answer { overflow: hidden; transition: max-height 0.45s cubic-bezier(.4,0,.2,1), opacity 0.35s ease; }
                .gr-card-hover { transition: transform 0.3s cubic-bezier(.22,1,.36,1), box-shadow 0.3s ease; }
                .gr-card-hover:hover { transform: translateY(-4px); box-shadow: 0 24px 48px rgba(157,0,30,0.12); }
                .gr-wave { clip-path: ellipse(55% 60% at 50% 100%); }
            `}</style>

            {/* ─── HERO ─── */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
                {/* Slide images */}
                {heroSlides.map((slide, i) => (
                    <div
                        key={i}
                        className="absolute inset-0 transition-all duration-1000 ease-in-out"
                        style={{
                            opacity: currentSlide === i ? 1 : 0,
                            zIndex: currentSlide === i ? 1 : 0,
                        }}
                    >
                        <img
                            src={slide.image}
                            alt=""
                            className="w-full h-full object-cover transition-all duration-1000 ease-in-out"
                            style={{
                                transform:
                                    currentSlide === i
                                        ? `scale(1.05) translateY(${scrollY * 0.08}px)`
                                        : "scale(1.15)",
                                filter: currentSlide === i ? "blur(0px)" : "blur(8px)",
                            }}
                        />

                        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/25" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    </div>
                ))}

                {/* Decorative shield watermark */}
                <div className="absolute right-8 top-1/2 -translate-y-1/2 opacity-[0.06] pointer-events-none select-none" style={{ zIndex: 1 }}>
                    <svg width="420" height="480" viewBox="0 0 100 110" fill="white">
                        <path d="M50 5 L90 22 L90 55 C90 78 70 95 50 105 C30 95 10 78 10 55 L10 22 Z" />
                    </svg>
                </div>

                {/* Hero content */}
                <div className="relative z-10 max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center w-full">
                    <div>
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/25 bg-white/10 backdrop-blur-sm mb-6">
                            <span className="w-2 h-2 rounded-full bg-red-400 gr-pulse"></span>
                            <span className="text-white/90 text-sm font-medium tracking-wide">
                                {heroSlides[currentSlide].badge}
                            </span>
                        </div>

                        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4 tracking-tight">
                            {heroSlides[currentSlide].title}
                            <br />
                            <span style={{ color: '#e8192c' }}>{heroSlides[currentSlide].highlight}</span>
                        </h1>

                        <p className="text-lg text-white/75 max-w-lg mb-8 leading-relaxed">
                            {heroSlides[currentSlide].sub}
                        </p>

                        <div className="flex flex-wrap gap-4">
                            <Link to="/contact"> <button
                                className="px-8 py-4 rounded-lg font-semibold text-white text-base"
                                style={{ background: '#9d001e' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#7d0018')}
                                onMouseLeave={e => (e.currentTarget.style.background = '#9d001e')}
                            >
                                Get a Free Quote →
                            </button></Link>
                            <Link to="/services">
                                <button className="px-8 py-4 rounded-lg font-semibold text-white border border-white/35 hover:bg-white/10 transition-colors text-base">
                                    Our Solutions
                                </button>
                            </Link>
                        </div>

                        {/* Slide dots */}
                        <div className="flex gap-2.5 mt-10">
                            {heroSlides.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setCurrentSlide(i)}
                                    className="rounded-full transition-all duration-300"
                                    style={{
                                        width: currentSlide === i ? 28 : 8,
                                        height: 8,
                                        background: currentSlide === i ? '#e8192c' : 'rgba(255,255,255,0.4)',
                                    }}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Hero card */}
                    <div className="hidden lg:block">
                        <div className="rounded-2xl p-6 backdrop-blur-md bg-white/10 border border-white/20">
                            <p className="text-white/60 text-xs uppercase tracking-widest mb-4">Quick Contact</p>
                            <div className="space-y-4">
                                {[
                                    { label: 'Phone / WhatsApp', value: '+233 24 2876 315', icon: '📞' },
                                    { label: 'Email', value: 'guardrisk consult@gmail.com', icon: '✉️' },
                                    { label: 'Website', value: 'www.guardrisk.org', icon: '🌐' },
                                ].map(item => (
                                    <div key={item.label} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                                        <span className="text-xl">{item.icon}</span>
                                        <div>
                                            <p className="text-white/50 text-xs">{item.label}</p>
                                            <p className="text-white font-medium text-sm">{item.value}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Link to="/contact">
                                <button
                                    className="w-full mt-5 py-3.5 rounded-lg text-white font-semibold text-sm"
                                    style={{ background: '#9d001e' }}
                                >
                                    Talk to an Advisor
                                </button>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Scroll indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 gr-float opacity-60">
                    <svg width="20" height="32" viewBox="0 0 20 32" fill="none">
                        <rect x="1" y="1" width="18" height="30" rx="9" stroke="white" strokeWidth="1.5" />
                        <circle cx="10" cy="9" r="3" fill="white" />
                    </svg>
                </div>
            </section>

            {/* ─── TAGLINE STRIP ─── */}
            <div style={{ background: '#9d001e' }} className="py-5 px-6 overflow-hidden">
                <div className="gr-marquee-track flex gap-16 whitespace-nowrap w-max">
                    {Array(8).fill(['More than Protection', '•', 'Independence & Objectivity', '•', 'Expert Claims Advocacy', '•', 'Tailored Risk Solutions', '•']).flat().map((t, i) => (
                        <span key={i} className="text-white/90 text-sm font-medium tracking-wider uppercase">{t}</span>
                    ))}
                </div>
            </div>

            {/* ─── WHAT WE DO ─── */}
            <section className="py-28 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <div className="gr-reveal">
                                <p className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: '#9d001e' }}>What We Do</p>
                                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
                                    Your Expert Insurance<br />Broker in Ghana
                                </h2>
                                <p className="text-gray-500 text-lg leading-relaxed mb-8">
                                    Guardrisk Limited is an independent insurance brokerage firm. We don't sell insurance — we source, negotiate, and secure the most suitable policies from reputable insurers, acting solely in your interest.
                                </p>
                            </div>
                            <div className="space-y-5">
                                {[
                                    { title: 'Risk Assessment & Advisory', desc: 'We identify, analyze, and evaluate your risks and recommend appropriate mitigation strategies.' },
                                    { title: 'Insurance Placement', desc: 'We source competitive policies from reputable insurers tailored to your specific exposure.' },
                                    { title: 'Claims Management & Advocacy', desc: 'We represent you throughout claims to ensure fair, timely settlement.' },
                                    { title: 'Ongoing Portfolio Management', desc: 'We continuously monitor and adjust your coverage as your business and life evolve.' },
                                ].map((item, i) => (
                                    <div key={i} className={`gr-reveal gr-delay-${i + 1} flex gap-4`}>
                                        <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center mt-0.5" style={{ background: '#fdf0f2' }}>
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path d="M3 8l3.5 3.5L13 4.5" stroke="#9d001e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-1">{item.title}</h4>
                                            <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="gr-reveal relative">
                            <div className="relative rounded-3xl overflow-hidden aspect-[4/5]">
                                <img
                                    src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&q=80"
                                    alt="Insurance consultation"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(157,0,30,0.5) 0%, transparent 60%)' }} />
                            </div>
                            {/* Floating stat card */}
                            <div className="absolute -left-6 bottom-10 bg-white rounded-2xl shadow-xl p-5 border border-gray-100">
                                <p className="text-3xl font-bold" style={{ color: '#9d001e' }}>12+</p>
                                <p className="text-gray-500 text-sm mt-1">Years of industry<br />expertise</p>
                            </div>
                            <div className="absolute -right-4 top-10 bg-white rounded-2xl shadow-xl p-4 border border-gray-100 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: '#fdf0f2' }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#9d001e" strokeWidth="2" strokeLinejoin="round" /></svg>
                                </div>
                                <div>
                                    <p className="font-bold text-gray-900 text-sm">Licensed Broker</p>
                                    <p className="text-gray-400 text-xs">NIC Regulated</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── SOLUTIONS TABS ─── */}
            <section className="py-28 px-6" style={{ background: '#fafafa' }}>
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-14 gr-reveal">
                        <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#9d001e' }}>Our Solutions</p>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Coverage for Every Risk</h2>
                        <p className="text-gray-500 text-lg max-w-2xl mx-auto">
                            Whether you're protecting a business empire or your family home, we have a solution built for you.
                        </p>
                    </div>

                    {/* Tab buttons */}
                    <div className="flex flex-wrap justify-center gap-3 mb-12 gr-reveal">
                        {solutionTabs.map((tab, i) => (
                            <button
                                key={i}
                                onClick={() => setActiveTab(i)}
                                className="gr-tab-btn px-6 py-3 rounded-xl font-semibold text-sm flex items-center gap-2"
                                style={{
                                    background: activeTab === i ? '#9d001e' : 'white',
                                    color: activeTab === i ? 'white' : '#6b7280',
                                    border: activeTab === i ? '1.5px solid #9d001e' : '1.5px solid #e5e7eb',
                                }}
                            >
                                <span>{tab.icon}</span>
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Tab content */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {solutionTabs[activeTab].items.map((item, i) => (
                            <div
                                key={`${activeTab}-${i}`}
                                className="gr-card-hover bg-white rounded-2xl p-6 border border-gray-100 cursor-pointer group"
                            >
                                <div className="w-10 h-10 rounded-lg mb-4 flex items-center justify-center" style={{ background: '#fdf0f2' }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#9d001e" strokeWidth="2" strokeLinejoin="round" /></svg>
                                </div>
                                <h4 className="font-bold text-gray-900 mb-2 group-hover:text-red-800 transition-colors">{item.name}</h4>
                                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                                <div className="mt-4 flex items-center gap-1 text-sm font-semibold" style={{ color: '#9d001e' }}>
                                    <Link to="/Contact">Learn More</Link>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── STATS ─── */}
            <section ref={statsRef} className="py-24 px-6" style={{ background: '#9d001e' }}>
                <div className="max-w-5xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
                        {[
                            { val: `${counters.years}+`, label: 'Years Experience' },
                            { val: `${counters.clients.toLocaleString()}+`, label: 'Clients Protected' },
                            { val: `${counters.coverage}B+ GHS`, label: 'Coverage Placed' },
                            { val: `${counters.satisfaction}%`, label: 'Client Satisfaction' },
                        ].map((s, i) => (
                            <div key={i} className="gr-reveal" style={{ transitionDelay: `${i * 0.12}s` }}>
                                <p className="text-4xl md:text-5xl font-bold text-white mb-2">{s.val}</p>
                                <p className="text-red-200 text-sm font-medium uppercase tracking-wider">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── WHY CHOOSE US ─── */}
            <section className="py-28 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16 gr-reveal">
                        <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#9d001e' }}>Why Choose Guardrisk</p>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">The GuardRisk Advantage</h2>
                        <p className="text-gray-500 text-lg max-w-2xl mx-auto">
                            We don't just sell policies — we build partnerships that protect your future.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {whyChooseUs.slice(0, 6).map((item, i) => (
                            <div
                                key={i}
                                className={`gr-reveal gr-delay-${(i % 3) + 1} gr-card-hover p-7 rounded-2xl border border-gray-100 bg-white`}
                            >
                                <div className="text-3xl mb-4">{item.icon}</div>
                                <h4 className="font-bold text-gray-900 mb-2 text-lg">{item.title}</h4>
                                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>

                    {/* 7th card — full width */}
                    <div className="gr-reveal mt-6 p-8 rounded-2xl border-2 flex flex-col md:flex-row items-center gap-6" style={{ borderColor: '#9d001e', background: '#fdf0f2' }}>
                        <div className="text-4xl">{whyChooseUs[6].icon}</div>
                        <div>
                            <h4 className="font-bold text-gray-900 mb-1 text-xl">{whyChooseUs[6].title}</h4>
                            <p className="text-gray-600 leading-relaxed">{whyChooseUs[6].desc}</p>
                        </div>
                        <Link to="/contact">
                            <button
                                className="flex-shrink-0 px-8 py-3.5 rounded-xl font-semibold text-white text-sm"
                                style={{ background: '#9d001e' }}
                            >
                                Work With Us
                            </button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* ─── SOCIAL PROOF / IMAGERY ─── */}
            <section className="py-28 px-6" style={{ background: '#111' }}>
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="gr-reveal">
                            <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#e8192c' }}>Our Commitment</p>
                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                                …More Than<br />
                                <span style={{ color: '#e8192c' }}>Protection</span>
                            </h2>
                            <p className="text-gray-400 text-lg leading-relaxed mb-8">
                                That's not just a tagline — it's our operating philosophy. When you partner with Guardrisk, you gain a dedicated ally who understands your risks, negotiates hard on your behalf, and stays with you through every claim.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                {['Policy Review & Optimisation', 'Insurance Training', 'Regulatory Guidance'].map(tag => (
                                    <span key={tag} className="px-4 py-2 rounded-full text-sm font-medium border" style={{ borderColor: '#9d001e', color: '#e8192c' }}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="gr-reveal grid grid-cols-2 gap-4">
                            {[
                                construction,
                                office,
                                marine,
                                ama2,
                            ].map((src, i) => (
                                <div key={i} className={`rounded-2xl overflow-hidden ${i === 1 ? 'mt-6' : ''} ${i === 3 ? '-mt-6' : ''}`}>
                                    <img src={src} alt="" className="w-full h-40 object-cover" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── FAQ ─── */}
            <section className="py-28 px-6 bg-white">
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-14 gr-reveal">
                        <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#9d001e' }}>FAQ</p>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Common Questions</h2>
                        <p className="text-gray-500 text-lg">Everything you need to know about working with Guardrisk.</p>
                    </div>

                    <div className="space-y-3">
                        {faqs.map((faq, i) => (
                            <div key={i} className="gr-reveal rounded-2xl border border-gray-100 overflow-hidden">
                                <button
                                    onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                                    className="w-full px-7 py-5 flex justify-between items-center text-left hover:bg-gray-50 transition-colors"
                                >
                                    <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                                    <span
                                        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-lg transition-transform duration-300"
                                        style={{
                                            background: '#9d001e',
                                            transform: openFAQ === i ? 'rotate(45deg)' : 'none',
                                        }}
                                    >
                                        +
                                    </span>
                                </button>
                                <div
                                    className="gr-faq-answer"
                                    style={{ maxHeight: openFAQ === i ? '200px' : '0px', opacity: openFAQ === i ? 1 : 0 }}
                                >
                                    <p className="px-7 pb-5 text-gray-500 leading-relaxed">{faq.a}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── CTA ─── */}
            <section className="py-28 px-6 relative overflow-hidden" style={{ background: '#9d001e' }}>
                {/* Decorative shield */}
                <div className="absolute right-0 top-0 opacity-10 pointer-events-none select-none">
                    <svg width="500" height="560" viewBox="0 0 100 110" fill="white">
                        <path d="M50 5 L90 22 L90 55 C90 78 70 95 50 105 C30 95 10 78 10 55 L10 22 Z" />
                    </svg>
                </div>

                <div className="max-w-3xl mx-auto text-center relative z-10 gr-reveal">
                    <p className="text-red-300 text-sm font-semibold uppercase tracking-widest mb-4">Get Started Today</p>
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                        Ready to Protect Your<br />Future?
                    </h2>
                    <p className="text-red-200 text-lg mb-10 max-w-xl mx-auto">
                        Talk to one of our expert advisors. We'll assess your risks, recommend the right coverage, and handle everything from placement to claims.
                    </p>

                    <div className="flex flex-wrap justify-center gap-4 mb-12">
                        <Link to="/contact">
                            <button className="px-10 py-4 rounded-xl font-bold text-base bg-white" style={{ color: '#9d001e' }}>
                                Get a Free Quote
                            </button>
                        </Link>
                        <a href="tel:+233242876315">
                            <button className="px-10 py-4 rounded-xl font-bold text-base text-white border-2 border-white/40 hover:bg-white/10 transition-colors">
                                Call +233 24 2876 315
                            </button>
                        </a>
                    </div>

                    <div className="flex flex-wrap justify-center gap-6 text-red-200 text-sm">
                        {['✓ No obligation', '✓ Expert advice', '✓ Fast turnaround', '✓ NIC Licensed'].map(t => (
                            <span key={t}>{t}</span>
                        ))}
                    </div>
                </div>
            </section>



        </div >
    )
}

export default Home